import type { BannerData, MarketingRuntimeConfig } from '../types';

export interface CampaignTrackingCommonProperties {
  page_name: string;
  market_activity_id: string;
  market_activity_name: string;
  page_id: string;
}

interface CampaignCouponMktExposureInput {
  moduleName: string;
  couponIndex: number;
  couponId?: number | string | null;
  couponName?: string | null;
}

interface CampaignCouponMktClickInput extends CampaignCouponMktExposureInput {
  buttonName: string;
}

interface CampaignButtonClickInput {
  moduleName: string;
  buttonName: string;
}

interface CampaignCollectButtonClickInput {
  moduleName: string;
  collectType: string;
  pitsIndex: number;
  productProperties: ReturnType<typeof buildCampaignProductTrackingProperties>;
}

interface CampaignProductTrackingItem {
  id?: number | string | null;
  short_name?: string | null;
  name?: string | null;
  sku?: string | null;
  business_sku?: string | null;
  shop_sku?: string | null;
  products_category?: string | null;
}

interface CampaignProductPriceInput {
  productPrice: number;
  currentProductPrice: number;
  baseCurrentProductPrice: number;
  dollarExchangeRate: number;
  currencyName: string;
}

export function getCampaignTrackingCommonProperties(
  runtimeConfig: MarketingRuntimeConfig,
): CampaignTrackingCommonProperties {
  return {
    page_name:
      runtimeConfig.page_name ||
      runtimeConfig.page_title ||
      runtimeConfig.activity_name ||
      '营销活动页',
    market_activity_id: `${runtimeConfig.activity_id || ''}`,
    market_activity_name: runtimeConfig.activity_name || '',
    page_id: `${runtimeConfig.marketing_page_id || ''}`,
  };
}

export function buildCampaignCouponMktExposureProperties(
  commonProperties: CampaignTrackingCommonProperties,
  couponInfo: CampaignCouponMktExposureInput,
) {
  return {
    ...commonProperties,
    module_name: couponInfo.moduleName,
    coupon_index: couponInfo.couponIndex,
    coupon_id: couponInfo.couponId == null ? '' : `${couponInfo.couponId}`,
    coupon_name: couponInfo.couponName || '',
  };
}

export function buildCampaignCouponMktClickProperties(
  commonProperties: CampaignTrackingCommonProperties,
  couponInfo: CampaignCouponMktClickInput,
) {
  return {
    ...buildCampaignCouponMktExposureProperties(commonProperties, couponInfo),
    btn_name: couponInfo.buttonName,
  };
}

export function buildCampaignButtonClickProperties(
  commonProperties: CampaignTrackingCommonProperties,
  buttonInfo: CampaignButtonClickInput,
) {
  return {
    ...commonProperties,
    module_name: buttonInfo.moduleName,
    btn_name: buttonInfo.buttonName,
  };
}

export function buildCampaignCollectButtonClickProperties(
  commonProperties: CampaignTrackingCommonProperties,
  collectInfo: CampaignCollectButtonClickInput,
) {
  return {
    ...commonProperties,
    module_name: collectInfo.moduleName,
    collect_type: collectInfo.collectType,
    pits_index: collectInfo.pitsIndex,
    ...collectInfo.productProperties,
  };
}

export function buildCampaignProductTrackingProperties(
  item: CampaignProductTrackingItem,
  priceInfo: CampaignProductPriceInput,
) {
  return {
    product_id: item.id == null ? '' : `${item.id}`,
    product_name: item.short_name || item.name || '',
    product_sku: item.sku || '',
    business_sku: item.business_sku || '',
    shop_sku: item.shop_sku || '',
    product_class_name: item.products_category || '',
    product_price: priceInfo.productPrice,
    current_product_price: priceInfo.currentProductPrice,
    base_current_product_price: priceInfo.baseCurrentProductPrice,
    dollar_exchange_rate: priceInfo.dollarExchangeRate,
    currency: priceInfo.currencyName,
  };
}

export function buildCampaignTopBannerAdExposureProperties(
  commonProperties: CampaignTrackingCommonProperties,
  jumpContent?: BannerData['jump_content'],
  pitsIndex = 0,
) {
  return {
    ...commonProperties,
    module_name: '顶部banner位',
    pits_index: pitsIndex,
    pits_jump_type: jumpContent?.jump_type == null ? '' : `${jumpContent.jump_type}`,
    pits_jump_content: jumpContent?.Jump_target == null ? '' : `${jumpContent.Jump_target}`,
  };
}

export function buildCampaignTopBannerAdExposureEvent(
  commonProperties: CampaignTrackingCommonProperties,
  jumpContent?: BannerData['jump_content'],
  pitsIndex = 0,
) {
  return {
    eventName: 'CampaignPageAD_Exp',
    properties: buildCampaignTopBannerAdExposureProperties(
      commonProperties,
      jumpContent,
      pitsIndex,
    ),
  };
}

export function buildCampaignTopBannerAdClickEvent(
  commonProperties: CampaignTrackingCommonProperties,
  jumpContent?: BannerData['jump_content'],
  pitsIndex = 0,
) {
  return {
    eventName: 'CampaignPageAD_Click',
    properties: buildCampaignTopBannerAdExposureProperties(
      commonProperties,
      jumpContent,
      pitsIndex,
    ),
  };
}

export function getCampaignFollowingAdExposureIndex(index: number) {
  return index + 1;
}
